import { Action, createReducer, on } from '@ngrx/store';
import { outcodeToRegionTableFilterDefaults } from '@platform/admin-tools/tables/outcode-to-region.table-definition';
import { affiliateTableFilterDefaults } from '@platform/affiliate/pages/affiliate-table/tables/affiliate.table-definition';
import { leadCaseTableFilterDefaults } from '@platform/case/tables/lead-case.table-definition';
import { mortgageClosedCaseTableFilterDefaults } from '@platform/case/tables/mortgage-closed-case.table-definition';
import { opportunityApprovalRequiredTableFilterDefaults } from '@platform/case/tables/opportunity-approval-required.table-definition';
import { opportunityTableFilterDefaults } from '@platform/case/tables/opportunity.table-definition';
import { pipelineCaseTableFilterDefaults } from '@platform/case/tables/pipeline-case.table-definition';
import { clientActivityLogTableFilterDefaults } from '@platform/client/pages/client-activity-log-table/tables/client-activity-log.table-definition';
import { clientTableFilterDefaults } from '@platform/client/pages/client-table/tables/client.table-definition';
import { companyTableFilterDefaults } from '@platform/company/tables/company.table-definition';
import { importTableFilterDefaults } from '@platform/import/tables/import.table-definition';
import { lenderGroupTableFilterDefaults } from '@platform/lender-group/pages/lender-group-table/tables/lender-group.table-definition';
import { lenderTableFilterDefaults } from '@platform/lender/tables/lender.table-definition';
import { mortgageDocAiTableFilterDefaults } from '@platform/mortgage-doc-ai/tables/mortgage-doc-ai.table-definition';
import { mortgageProductTableFilterDefaults } from '@platform/mortgage-product/tables/mortgage-product.table-definition';
import { mortgageTableFilterDefaults } from '@platform/mortgage/pages/mortgage-table/tables/mortgage.table-definition';
import { preCompletionTableFilterDefaults } from '@platform/mortgage/tables/pre-completion.table-definition';
import { propertyTableFilterDefaults } from '@platform/property/pages/property-table/tables/property.table-definition';
import { referralCodeTableFilterDefaults } from '@platform/referral-code/tables/referral-code.table-definition';
import { tinyUrlTableFilterDefaults } from '@platform/tiny-url/tables/tiny-url.table-definition';
import { userTableFilterDefaults } from '@platform/user/pages/user-table/tables/user.table-definition';
import { TablesState } from '@shared/models/app-state.model';
import { BaseTableModel } from '@shared/models/tables.model';
import {
  affiliateTableActions,
  clientActivityLogTableActions,
  clientCaseTableActions,
  clientTableActions,
  companyTableActions,
  importTableActions,
  lenderGroupTableActions,
  lenderTableActions,
  mortgageClosedCaseTableActions,
  mortgageDocAiTableActions,
  mortgageLeadTableActions,
  mortgagePipelineCaseTableActions,
  mortgageProductTableActions,
  mortgageTableActions,
  networkTableActions,
  opportunityApproveRequiredTableActions,
  opportunityTableActions,
  outcodeToRegionTableActions,
  preCompletionTableActions,
  propertyTableActions,
  referralCodesTableActions,
  tinyUrlsTableActions,
  userTableActions,
} from '@shared/store/table/table.action';
import { clientCasesTableFilterDefaults } from '../../../platform/client/components/client-cases-table/tables/client-cases.table-definition';
import { TablesEnum } from './tables.enum';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createTableState = (filterDefaults): BaseTableModel<any, any, any> => ({
  filter: filterDefaults,
  sort: null,
  pagination: {
    pageNumber: 0,
    pageSize: 10,
  },
  selectedRows: [],
});

const initialState: TablesState = {
  [TablesEnum.UserTable]: createTableState(userTableFilterDefaults),
  [TablesEnum.ClientTable]: createTableState(clientTableFilterDefaults),
  [TablesEnum.PropertyTable]: createTableState(propertyTableFilterDefaults),
  [TablesEnum.CompanyTable]: createTableState(companyTableFilterDefaults),
  [TablesEnum.MortgageTable]: createTableState(mortgageTableFilterDefaults),
  [TablesEnum.OpportunityApprovalRequiredTable]: createTableState(opportunityApprovalRequiredTableFilterDefaults),
  [TablesEnum.OpportunityTable]: createTableState(opportunityTableFilterDefaults),
  [TablesEnum.MortgagePipelineCaseTable]: createTableState(pipelineCaseTableFilterDefaults),
  [TablesEnum.MortgageClosedCaseTable]: createTableState(mortgageClosedCaseTableFilterDefaults),
  [TablesEnum.LeadCaseTable]: createTableState(leadCaseTableFilterDefaults),
  [TablesEnum.ClientCaseTable]: createTableState(clientCasesTableFilterDefaults),
  [TablesEnum.OutcodeToRegionTable]: createTableState(outcodeToRegionTableFilterDefaults),
  [TablesEnum.LenderTable]: createTableState(lenderTableFilterDefaults),
  [TablesEnum.LenderGroupTable]: createTableState(lenderGroupTableFilterDefaults),
  [TablesEnum.MortgageDocAiTable]: createTableState(mortgageDocAiTableFilterDefaults),
  [TablesEnum.TinyUrlsTable]: createTableState(tinyUrlTableFilterDefaults),
  [TablesEnum.PreCompletionTable]: createTableState(preCompletionTableFilterDefaults),
  [TablesEnum.ReferralCodeTable]: createTableState(referralCodeTableFilterDefaults),
  [TablesEnum.ImportTable]: createTableState(importTableFilterDefaults),
  [TablesEnum.AffiliateTable]: createTableState(affiliateTableFilterDefaults),
  [TablesEnum.MortgageProductTable]: createTableState(mortgageProductTableFilterDefaults),
  [TablesEnum.NetworkTable]: createTableState(undefined),
  [TablesEnum.ClientActivityLogTable]: createTableState(clientActivityLogTableFilterDefaults),
};

const reducer = createReducer(
  initialState,
  //Paging actions
  on(
    userTableActions.setPagination,
    clientCaseTableActions.setPagination,
    companyTableActions.setPagination,
    opportunityApproveRequiredTableActions.setPagination,
    opportunityTableActions.setPagination,
    mortgageLeadTableActions.setPagination,
    opportunityApproveRequiredTableActions.setPagination,
    mortgageLeadTableActions.setPagination,
    opportunityTableActions.setPagination,
    mortgageClosedCaseTableActions.setPagination,
    mortgagePipelineCaseTableActions.setPagination,
    clientTableActions.setPagination,
    propertyTableActions.setPagination,
    mortgageTableActions.setPagination,
    outcodeToRegionTableActions.setPagination,
    lenderTableActions.setPagination,
    lenderGroupTableActions.setPagination,
    mortgageDocAiTableActions.setPagination,
    tinyUrlsTableActions.setPagination,
    preCompletionTableActions.setPagination,
    referralCodesTableActions.setPagination,
    importTableActions.setPagination,
    affiliateTableActions.setPagination,
    mortgageProductTableActions.setPagination,
    networkTableActions.setPagination,
    clientActivityLogTableActions.setPagination,
    (state, { tableName, pagination }) => {
      const tableState = state[tableName];
      return { ...state, [tableName]: { ...tableState, pagination } };
    },
  ),
  //Sorting actions
  on(
    userTableActions.setSorting,
    clientCaseTableActions.setSorting,
    companyTableActions.setSorting,
    opportunityApproveRequiredTableActions.setSorting,
    opportunityTableActions.setSorting,
    mortgageLeadTableActions.setSorting,
    opportunityApproveRequiredTableActions.setSorting,
    mortgageLeadTableActions.setSorting,
    opportunityTableActions.setSorting,
    mortgageClosedCaseTableActions.setSorting,
    mortgagePipelineCaseTableActions.setSorting,
    clientTableActions.setSorting,
    propertyTableActions.setSorting,
    mortgageTableActions.setSorting,
    outcodeToRegionTableActions.setSorting,
    lenderTableActions.setSorting,
    lenderGroupTableActions.setSorting,
    tinyUrlsTableActions.setSorting,
    preCompletionTableActions.setSorting,
    referralCodesTableActions.setSorting,
    importTableActions.setSorting,
    mortgageProductTableActions.setSorting,
    clientActivityLogTableActions.setSorting,
    (state, { tableName, sort }) => {
      const tableState = state[tableName];
      return { ...state, [tableName]: { ...tableState, sort } };
    },
  ),
  //Filter actions
  on(
    userTableActions.setFilter,
    clientCaseTableActions.setFilter,
    companyTableActions.setFilter,
    opportunityApproveRequiredTableActions.setFilter,
    opportunityTableActions.setFilter,
    mortgageLeadTableActions.setFilter,
    opportunityApproveRequiredTableActions.setFilter,
    mortgageLeadTableActions.setFilter,
    opportunityTableActions.setFilter,
    mortgageClosedCaseTableActions.setFilter,
    mortgagePipelineCaseTableActions.setFilter,
    clientTableActions.setFilter,
    propertyTableActions.setFilter,
    mortgageTableActions.setFilter,
    outcodeToRegionTableActions.setFilter,
    lenderTableActions.setFilter,
    lenderGroupTableActions.setFilter,
    mortgageDocAiTableActions.setFilter,
    tinyUrlsTableActions.setFilter,
    preCompletionTableActions.setFilter,
    referralCodesTableActions.setFilter,
    importTableActions.setFilter,
    affiliateTableActions.setFilter,
    mortgageProductTableActions.setFilter,
    clientActivityLogTableActions.setFilter,
    (state, { tableName, filter }) => {
      const tableState = state[tableName];
      return {
        ...state,
        [tableName]: {
          ...tableState,
          filter,
          selectedRows: [],
          pagination: { pageSize: tableState.pagination.pageSize, pageNumber: 0 },
        },
      };
    },
  ),
  //Selected rows actions
  on(
    userTableActions.setSelectedRows,
    opportunityApproveRequiredTableActions.setSelectedRows,
    opportunityTableActions.setSelectedRows,
    mortgageLeadTableActions.setSelectedRows,
    mortgagePipelineCaseTableActions.setSelectedRows,
    clientTableActions.setSelectedRows,
    mortgageTableActions.setSelectedRows,
    referralCodesTableActions.setSelectedRows,
    (state, { tableName, entity }) => {
      const tableState = state[tableName];
      const selectedRows = [...entity];
      return { ...state, [tableName]: { ...tableState, selectedRows } };
    },
  ),

  on(
    userTableActions.resetTableFilters,
    clientCaseTableActions.resetTableFilters,
    companyTableActions.resetTableFilters,
    opportunityApproveRequiredTableActions.resetTableFilters,
    opportunityTableActions.resetTableFilters,
    mortgageLeadTableActions.resetTableFilters,
    opportunityApproveRequiredTableActions.resetTableFilters,
    mortgageLeadTableActions.resetTableFilters,
    opportunityTableActions.resetTableFilters,
    mortgageClosedCaseTableActions.resetTableFilters,
    mortgagePipelineCaseTableActions.resetTableFilters,
    clientTableActions.resetTableFilters,
    mortgageTableActions.resetTableFilters,
    outcodeToRegionTableActions.resetTableFilters,
    lenderTableActions.resetTableFilters,
    lenderGroupTableActions.resetTableFilters,
    mortgageDocAiTableActions.resetTableFilters,
    tinyUrlsTableActions.resetTableFilters,
    preCompletionTableActions.resetTableFilters,
    referralCodesTableActions.resetTableFilters,
    importTableActions.resetTableFilters,
    affiliateTableActions.resetTableFilters,
    mortgageProductTableActions.resetTableFilters,
    clientActivityLogTableActions.resetTableFilters,
    (state, { tableName, entity }) => {
      const tableState = state[tableName];
      return {
        ...state,
        [tableName]: {
          ...tableState,
          selectedRows: [],
          filter: entity,
          pagination: { pageSize: tableState.pagination.pageSize, pageNumber: 0 },
        },
      };
    },
  ),
);

export const tableReducer = (state: TablesState = initialState, action: Action): TablesState => reducer(state, action);
